import { orderBy } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

import { Spinner, Tooltip } from 'components/ui';
import {
  getAttrsDistance,
  getObservationGroupByInspectionId,
  getPictureTargetDistance,
  getPrimaryObservationForDamage,
  getPrimaryObservationGroupForDamage,
  resolveCompareData,
} from 'horizon/components/Damages/utils';
import { ThumbnailCarousel } from 'horizon/components/Images/ImageThumbnail/ThumbnailCarousel';
import {
  type AtlasGqlHorizonDamage,
  type AtlasGqlHorizonDamageObservation,
  type AtlasGqlPicture,
  useGetHorizonDamagePictureQuery,
  useGetPictureLazyQuery,
} from 'types/atlas-graphql';
import { WORK_ORDER_COLLABORATOR } from 'utils/access-control/roles';
import { isExternalInspection } from 'utils/constants';
import { usePermissions } from 'utils/usePermissions';

import { useCompareNavigation } from '../useCompareNavigation';

import {
  StyledCarouselWrapper,
  StyledDiv,
  StyledQuestionCircleTwoTone,
} from './HorizonDamageImage.style';
import { type CompareTarget, type PreviousDamageInfo, type PreviousInspectionInfo } from './types';
import { getPreviousDamageDetails, getSelectedCompareData } from './utils';

type ImageProps = {
  damageId: string;
  damage?: AtlasGqlHorizonDamage;
  inspectionIdOfGroupToRender?: string;
  expandToInspectionId?: string;
  shouldShowExpand?: boolean;
  shouldShowDistance?: boolean;
  primaryIndicator?: React.ReactNode;
  compareTarget?: CompareTarget | null;
  fetchCompareDamagesData?: boolean;
};

export const HorizonDamageImage: React.FunctionComponent<ImageProps> = ({
  damageId,
  damage: _damage,
  inspectionIdOfGroupToRender,
  expandToInspectionId,
  shouldShowExpand,
  shouldShowDistance,
  primaryIndicator,
  compareTarget,
  fetchCompareDamagesData,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { hasRole } = usePermissions();
  const isWoCollaborator = hasRole(WORK_ORDER_COLLABORATOR);
  const [fetchedGroup, setFetchedGroup] = useState<{
    id: string | undefined;
    observations: AtlasGqlHorizonDamageObservation[];
  }>();
  const [prevInspectionDetails, setPrevInspectionDetails] = useState<PreviousInspectionInfo>();
  const [prevDamageDetails, setPrevDamageDetails] = useState<PreviousDamageInfo>();
  const { comparePictures, loading: inspectionPicturesLoading } = useCompareNavigation();

  // If damage prop is not provided (currently used on Task Details), fetch picture and damage data together
  const { data, loading } = useGetHorizonDamagePictureQuery({
    variables: {
      input: { id: damageId },
      ...(fetchCompareDamagesData ? { fetchCompareDamagesData } : {}),
    },
    skip: !!_damage,
  });

  /**
   * If damage prop is provided, lazy query will be used to fetch necessary pictures. This is used
   * by the damage table expanded row to prevent undesired refetches/caching issues caused by having
   * multiple components fetch different shapes of damage data.
   */
  const [getPicture] = useGetPictureLazyQuery();

  const damage =
    _damage ?? (data?.getHorizonDamage && (data?.getHorizonDamage as AtlasGqlHorizonDamage));

  useEffect(() => {
    const primaryObs = getPrimaryObservationForDamage(damage);
    const compareData = resolveCompareData({
      isExternalInspection:
        isExternalInspection(primaryObs?.inspection?.type?.name) && !!fetchCompareDamagesData,
      previousDamageInfo: getPreviousDamageDetails(damage?.observationGroups ?? []) ?? undefined,
      sourceObservation: primaryObs,
    });
    setPrevDamageDetails(compareData?.previousDamageInfo);
    setPrevInspectionDetails(compareData?.prevInspectionDetails);
  }, [damage, fetchCompareDamagesData]);

  const observationGroup =
    (damage &&
      inspectionIdOfGroupToRender &&
      getObservationGroupByInspectionId(damage, inspectionIdOfGroupToRender)) ||
    getPrimaryObservationGroupForDamage(damage);
  const _observations = useMemo(
    () =>
      orderBy(
        observationGroup?.observations?.filter((o): o is AtlasGqlHorizonDamageObservation => !!o),
        [
          observation =>
            getAttrsDistance(observation?.attrs) ??
            getPictureTargetDistance(observation?.picture?.target),
          observation => observation?.attrs?.Severity ?? 0,
        ],
        ['asc', 'desc']
      ),
    [observationGroup]
  );

  const observationGroupId = observationGroup?.id;
  useEffect(() => {
    if (!_damage) {
      return;
    }

    let isCurrent = true;
    const fetchAndMergePictures = async () => {
      const pictures = (
        await Promise.all(
          _observations.map((o: AtlasGqlHorizonDamageObservation) =>
            getPicture({ variables: { pictureId: o.picture?.id } })
          )
        )
      ).map(({ data }: any) => data?.picture);

      if (!isCurrent) {
        return;
      }

      setFetchedGroup({
        id: observationGroupId,
        observations: _observations.map((o: AtlasGqlHorizonDamageObservation) => {
          const picture = pictures.find((p: any) => p?.id === o.picture?.id);

          if (picture) {
            return { ...o, picture: picture as unknown as AtlasGqlPicture };
          }

          return o;
        }),
      });
    };

    fetchAndMergePictures();

    return () => {
      isCurrent = false;
    };
  }, [_observations, _damage, getPicture, observationGroupId]);

  // The carousel reads its starting slide only on mount, so it must not mount before this group's
  // observations are available.
  const observations = !_damage
    ? _observations
    : fetchedGroup?.id === observationGroupId
      ? fetchedGroup?.observations
      : undefined;

  const compareData =
    compareTarget === undefined
      ? { previousDamageInfo: prevDamageDetails, prevInspectionDetails }
      : getSelectedCompareData(observationGroup, compareTarget);

  const sourceObservationIndex = Math.max(
    0,
    observations?.findIndex(o => o.id === observationGroup?.sourceObservationId) ?? 0
  );

  return (
    <StyledDiv>
      {loading || !observations ? (
        <Spinner />
      ) : (
        <StyledCarouselWrapper data-testid="horizon-damage-carousel" $innerHeight={'unset'}>
          <ThumbnailCarousel
            key={observationGroupId}
            thumbnails={observations.map(o => ({
              picture: o.picture,
              observations: [o],
              scaleFilter: 1,
              expandToInspectionId,
              primaryIndicator,
            }))}
            initialSlideIndex={sourceObservationIndex}
            allowZoom={true}
            shouldShowExpand={shouldShowExpand}
            shouldShowDistance={shouldShowDistance}
            previousDamageInfo={compareData.previousDamageInfo}
            prevInspectionDetails={compareData.prevInspectionDetails}
            handleCompareNavigation={comparePictures}
            compareInspectionPicturesLoading={inspectionPicturesLoading}
          />
          {!isWoCollaborator && (damage?.observationGroups ?? []).length > 1 && (
            <Tooltip
              title={t('damage.primaryGroupImageTooltip', {
                count: observations.length,
                atCreation: inspectionIdOfGroupToRender
                  ? t('damage.atTimeOfTaskCreation', ' at the time of task creation')
                  : '',
              })}
            >
              <StyledQuestionCircleTwoTone
                twoToneColor={theme.teal}
                $isInCarousel={observations.length > 1}
              />
            </Tooltip>
          )}
        </StyledCarouselWrapper>
      )}
    </StyledDiv>
  );
};
